import React, { useState } from 'react';
import API from '../../../services/api';
import { API_BASE_URL } from '../../../constants/config';

// Helper to get correct root URL (removes /api from the end if present)
const getServerRootUrl = () => {
    return API_BASE_URL.replace(/\/api\/?$/, '');
};

const CompanyManagement = ({ companies, fetchCompanies }) => {
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', address: '', phone: '', ntn: '', strn: '' });
    const [headerLogo, setHeaderLogo] = useState(null);
    const [footerLogo, setFooterLogo] = useState(null);
    
    // State for Image Modal Preview
    const [modalImage, setModalImage] = useState(null);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('name', formData.name);
        data.append('address', formData.address);
        data.append('phone', formData.phone);
        data.append('ntn', formData.ntn);
        data.append('strn', formData.strn);
        
        if (headerLogo) {
            data.append('header_logo', headerLogo);
        }
        if (footerLogo) {
            data.append('footer_logo', footerLogo);
        }

        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            };

            if (editingId) {
                await API.put(`/companies/${editingId}`, data, config);
                alert('Company updated successfully!');
            } else {
                await API.post('/companies', data, config);
                alert('Company created successfully!');
            }
            resetForm();
            fetchCompanies();
        } catch (err) {
            console.error('Frontend Error:', err.response?.data || err.message);
            alert('Error saving company: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleEdit = (company) => {
        setEditingId(company.id);
        setFormData({ 
            name: company.name, 
            address: company.address, 
            phone: company.phone, 
            ntn: company.ntn || '', 
            strn: company.strn || '' 
        });
        setHeaderLogo(null);
        setFooterLogo(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this company?')) return;
        try {
            await API.delete(`/companies/${id}`);
            alert('Company deleted!');
            fetchCompanies();
        } catch (err) {
            alert('Error deleting company');
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ name: '', address: '', phone: '', ntn: '', strn: '' });
        setHeaderLogo(null);
        setFooterLogo(null);
    };

    const serverRoot = getServerRootUrl();

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.headerTitleContainer}>
                    <h3 style={styles.mainTitle}>{editingId ? '✏️ Edit Company Details' : '➕ Add New Company'}</h3>
                    <p style={styles.subTitle}>Manage company headers, footers, NTN, STRN, and contact info for invoices.</p>
                </div>

                <form onSubmit={handleFormSubmit} style={styles.form}>
                    <div style={styles.row}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Company Name</label>
                            <input 
                                type="text" 
                                placeholder="Enter company name" 
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Phone Number</label>
                            <input 
                                type="text" 
                                placeholder="Enter phone number" 
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                required
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <div style={styles.row}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>NTN No / STN</label>
                            <input 
                                type="text" 
                                placeholder="Enter NTN or STN number" 
                                value={formData.ntn}
                                onChange={(e) => setFormData({...formData, ntn: e.target.value})}
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>STRN No</label>
                            <input 
                                type="text" 
                                placeholder="Enter STRN number" 
                                value={formData.strn}
                                onChange={(e) => setFormData({...formData, strn: e.target.value})}
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <div style={styles.inputGroup}>
                        <label style={styles.label}>Complete Address</label>
                        <textarea 
                            placeholder="Enter company address..." 
                            value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                            required
                            style={{...styles.input, height: '80px', resize: 'vertical'}}
                        />
                    </div>

                    <div style={styles.row}>
                        <div style={styles.fileUploadBox}>
                            <label style={styles.label}>Header Logo (Top)</label>
                            <input type="file" onChange={(e) => setHeaderLogo(e.target.files[0])} accept="image/*" style={styles.fileInput} />
                        </div>
                        <div style={styles.fileUploadBox}>
                            <label style={styles.label}>Footer Logo (Bottom)</label>
                            <input type="file" onChange={(e) => setFooterLogo(e.target.files[0])} accept="image/*" style={styles.fileInput} />
                        </div>
                    </div>

                    <div style={styles.buttonContainer}>
                        <button type="submit" style={styles.submitBtn}>
                            {editingId ? 'Update Company' : 'Save Company'}
                        </button>
                        {editingId && (
                            <button type="button" onClick={resetForm} style={styles.cancelBtn}>
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div style={{...styles.card, marginTop: '30px'}}>
                <h3 style={styles.mainTitle}>🏢 Registered Companies ({companies.length})</h3>
                
                <div style={styles.tableResponsive}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Header Logo</th>
                                <th style={styles.th}>Company Name</th>
                                <th style={styles.th}>NTN / STRN</th>
                                <th style={styles.th}>Phone & Address</th>
                                <th style={styles.th}>Footer Logo</th>
                                <th style={{...styles.th, textAlign: 'center'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={styles.noData}>No companies found. Add one above!</td>
                                </tr>
                            ) : (
                                companies.map((c) => {
                                    const headerImageUrl = c.header_logo ? `${serverRoot}/uploads/${c.header_logo}` : null;
                                    const footerImageUrl = c.footer_logo ? `${serverRoot}/uploads/${c.footer_logo}` : null;

                                    return (
                                        <tr key={c.id} style={styles.tr}>
                                            <td style={styles.td}>
                                                {headerImageUrl ? (
                                                    <div 
                                                        style={styles.logoWrapper} 
                                                        onClick={() => setModalImage(headerImageUrl)}
                                                        title="Click to view original size"
                                                    >
                                                        <img src={headerImageUrl} alt="Header" style={styles.logoImg} />
                                                    </div>
                                                ) : <span style={styles.noLogo}>No Logo</span>}
                                            </td>
                                            <td style={styles.td}><strong style={{color: '#1e293b'}}>{c.name}</strong></td>
                                            <td style={styles.td}>
                                                <div style={{fontSize: '12px'}}><strong>NTN:</strong> {c.ntn || 'N/A'}</div>
                                                <div style={{fontSize: '12px'}}><strong>STRN:</strong> {c.strn || 'N/A'}</div>
                                            </td>
                                            <td style={{...styles.td, maxWidth: '250px'}}>
                                                <div style={{fontSize: '12px', fontWeight: '600'}}>{c.phone}</div>
                                                <div style={{fontSize: '12px', color: '#64748b'}}>{c.address}</div>
                                            </td>
                                            <td style={styles.td}>
                                                {footerImageUrl ? (
                                                    <div 
                                                        style={styles.logoWrapper} 
                                                        onClick={() => setModalImage(footerImageUrl)}
                                                        title="Click to view original size"
                                                    >
                                                        <img src={footerImageUrl} alt="Footer" style={styles.logoImg} />
                                                    </div>
                                                ) : <span style={styles.noLogo}>No Logo</span>}
                                            </td>
                                            <td style={{...styles.td, textAlign: 'center'}}>
                                                <div style={styles.actionGroup}>
                                                    <button onClick={() => handleEdit(c)} style={styles.editBtn}>Edit</button>
                                                    <button onClick={() => handleDelete(c.id)} style={styles.deleteBtn}>Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* FULL SIZE IMAGE MODAL */}
            {modalImage && (
                <div style={styles.modalOverlay} onClick={() => setModalImage(null)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button style={styles.closeBtn} onClick={() => setModalImage(null)}>×</button>
                        <img src={modalImage} alt="Original Preview" style={styles.originalImg} />
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { display: 'flex', flexDirection: 'column', gap: '20px' },
    card: { background: '#ffffff', borderRadius: '14px', padding: '25px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' },
    headerTitleContainer: { marginBottom: '20px' },
    mainTitle: { margin: '0 0 5px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' },
    subTitle: { margin: 0, fontSize: '13px', color: '#64748b' },
    form: { display: 'flex', flexDirection: 'column', gap: '16px' },
    row: { display: 'flex', gap: '20px', flexWrap: 'wrap' },
    inputGroup: { flex: 1, minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#475569' },
    input: { padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', transition: 'all 0.2s', backgroundColor: '#fcfcfc' },
    fileUploadBox: { flex: 1, minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '6px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px dashed #cbd5e1' },
    fileInput: { fontSize: '13px', color: '#64748b' },
    buttonContainer: { display: 'flex', gap: '10px', marginTop: '5px' },
    submitBtn: { padding: '11px 24px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)' },
    cancelBtn: { padding: '11px 20px', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' },
    tableResponsive: { width: '100%', overflowX: 'auto', marginTop: '15px' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' },
    th: { background: '#f8fafc', color: '#475569', padding: '14px 16px', fontWeight: '600', borderBottom: '2px solid #e2e8f0' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' },
    td: { padding: '14px 16px', color: '#334155', verticalAlign: 'middle' },
    logoWrapper: { width: '45px', height: '45px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' },
    logoImg: { width: '100%', height: '100%', objectFit: 'contain' },
    noLogo: { fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' },
    noData: { textAlign: 'center', padding: '30px', color: '#94a3b8', fontStyle: 'italic' },
    actionGroup: { display: 'flex', gap: '8px', justifyContent: 'center' },
    editBtn: { padding: '6px 14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px', boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)' },
    deleteBtn: { padding: '6px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px', boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)' },
    
    // Modal Styles
    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
    modalContent: { background: '#fff', padding: '20px', borderRadius: '12px', position: 'relative', maxWidth: '90%', maxHeight: '90%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' },
    originalImg: { maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '6px' },
    closeBtn: { position: 'absolute', top: '10px', right: '10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '30px', height: '30px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

export default CompanyManagement;